import { difference, uniq, without } from 'es-toolkit';
import { createSlice, Draft, isAnyOf, PayloadAction } from '@reduxjs/toolkit';

import {
  componentDesignsMoved,
  getMovedComponentInsertions,
  PathUtilities,
  projectArchived,
  projectCreated,
  projectSynchronized,
} from '@paragon/layout-models';

import { SortableColumnName } from '../grid/ordering';
import {
  assigneeChanged,
  createdMonthChanged,
  filtersCleared,
  searchChanged,
  statusChanged,
} from './projectFiltersSlice';

export interface GridState {
  readonly expandedRows: readonly string[];
  readonly checkedRows: readonly string[];
  readonly selectedParentGuid: string | null;
  readonly selectedPath: string | null;

  /* Tracks the last individually-checked row so a subsequent shift-click can fill in the whole range. */
  readonly checkAnchor: string | null;

  /** The project whose detail drawer is open; null when the drawer is closed. */
  readonly openProjectGuid: string | null;

  /** Focus the project name field when the drawer opens (set when the project was just created). */
  readonly focusProjectNameOnDrawerOpen: boolean;
  readonly loadingSealsForProjects: readonly string[];

  readonly order: 'asc' | 'desc';
  readonly orderByColumnName: SortableColumnName;

  readonly pageIndex: number;
}

const initialState: GridState = {
  expandedRows: [],
  checkedRows: [],
  selectedPath: null,
  selectedParentGuid: null,
  checkAnchor: null,
  openProjectGuid: null,
  focusProjectNameOnDrawerOpen: false,
  loadingSealsForProjects: [],
  order: 'desc',
  orderByColumnName: 'createdAt',
  pageIndex: 0,
};

/**
 * The check anchor must always refer to a currently-checked row; call this after any reducer that removes or remaps
 *   checked rows to clear an anchor that no longer qualifies.
 */
function reconcileCheckAnchor(state: Draft<GridState>) {
  if (state.checkAnchor != null && !state.checkedRows.includes(state.checkAnchor)) {
    state.checkAnchor = null;
  }
}

const gridSlice = createSlice({
  name: 'grid',
  initialState,
  reducers: {
    rowExpanded: (
      state,
      action: PayloadAction<{ readonly rowPath: string; readonly descendants: readonly string[] }>,
    ) => {
      state.expandedRows = uniq(state.expandedRows.concat([action.payload.rowPath]));
    },
    rowsExpanded: (
      state,
      action: PayloadAction<{ readonly rowPaths: string[]; readonly descendants: readonly string[] }>,
    ) => {
      state.expandedRows = uniq(state.expandedRows.concat(action.payload.rowPaths));
    },
    rowCollapsed: (
      state,
      action: PayloadAction<{ readonly rowPath: string; readonly descendants: readonly string[] }>,
    ) => {
      state.expandedRows = without(state.expandedRows, action.payload.rowPath);

      // Deselect descendants of the collapsed row
      if (state.selectedPath && action.payload.descendants.includes(state.selectedPath)) {
        state.selectedPath = null;
        state.selectedParentGuid = null;
      }
    },
    rowsCollapsed: (
      state,
      action: PayloadAction<{ readonly rowPaths: string[]; readonly descendants: readonly string[] }>,
    ) => {
      state.expandedRows = without(state.expandedRows, ...action.payload.rowPaths);

      // Deselect descendants of the collapsed rows
      if (state.selectedPath && action.payload.descendants.includes(state.selectedPath)) {
        state.selectedPath = null;
        state.selectedParentGuid = null;
      }
    },
    rowsChecked: (
      state,
      action: PayloadAction<{ readonly rowPaths: readonly string[]; readonly descendants: readonly string[] }>,
    ) => {
      state.checkedRows = uniq(state.checkedRows.concat(action.payload.rowPaths, action.payload.descendants));
    },
    rowCheckToggled: (
      state,
      action: PayloadAction<{ readonly rowPath: string; readonly descendants: readonly string[] }>,
    ) => {
      if (state.checkedRows.includes(action.payload.rowPath)) {
        state.checkedRows = difference(state.checkedRows, [action.payload.rowPath].concat(action.payload.descendants));
        state.checkAnchor = null;
      } else {
        state.checkedRows = uniq(state.checkedRows.concat([action.payload.rowPath], action.payload.descendants));
        state.checkAnchor = action.payload.rowPath;
      }
    },
    checkedRowsCleared: (state) => {
      state.checkedRows = [];
      reconcileCheckAnchor(state);
    },
    rowsUnchecked: (
      state,
      action: PayloadAction<{ readonly rowPaths: string[]; readonly descendants: readonly string[] }>,
    ) => {
      state.checkedRows = difference(state.checkedRows, action.payload.rowPaths.concat(action.payload.descendants));
      reconcileCheckAnchor(state);
    },
    allRowsCollapsed: (state) => {
      state.expandedRows = [];
    },
    rowSelected: (state, action: PayloadAction<{ readonly rowPath: string; readonly parentGuid: string | null }>) => {
      state.selectedPath = action.payload.rowPath;
      state.selectedParentGuid = action.payload.parentGuid;

      // Selecting a row also opens (or retargets) the detail drawer on that row's project.
      state.openProjectGuid = PathUtilities.getProjectGuid(action.payload.rowPath);
      state.focusProjectNameOnDrawerOpen = false;
    },
    detailDrawerClosed: (state) => {
      state.openProjectGuid = null;
      state.focusProjectNameOnDrawerOpen = false;
    },
    projectNameFocusConsumed: (state) => {
      state.focusProjectNameOnDrawerOpen = false;
    },
    sealLoadingStartedForProject: (state, action: PayloadAction<{ readonly projectGuid: string }>) => {
      state.loadingSealsForProjects = uniq(state.loadingSealsForProjects.concat(action.payload.projectGuid));
    },
    sealLoadingFinishedForProject: (state, action: PayloadAction<{ readonly projectGuid: string }>) => {
      state.loadingSealsForProjects = without(state.loadingSealsForProjects, action.payload.projectGuid);
    },
    sortColumnToggled: (state, action: PayloadAction<{ readonly columnName: SortableColumnName }>) => {
      const isAsc = state.orderByColumnName === action.payload.columnName && state.order === 'asc';
      state.order = isAsc ? 'desc' : 'asc';
      state.orderByColumnName = action.payload.columnName;
    },
    pageChanged: (state, action: PayloadAction<{ readonly pageIndex: number }>) => {
      state.pageIndex = action.payload.pageIndex;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(projectCreated, (state, action) => {
      state.selectedPath = action.payload.model.guid;
      state.selectedParentGuid = null;

      // Open the drawer on the new project with the name field focused so the user can name it right away.
      state.openProjectGuid = action.payload.model.guid;
      state.focusProjectNameOnDrawerOpen = true;
    });

    builder.addCase(componentDesignsMoved, (state, action) => {
      state.expandedRows = uniq(state.expandedRows.concat(action.payload.pathsToTargetContainer));
      state.checkedRows = uniq(
        state.checkedRows.flatMap((checkedRow) => {
          for (const movedComponentInsertion of getMovedComponentInsertions(action.payload)) {
            const oldComponentPath = PathUtilities.createFromInsertion(movedComponentInsertion);

            if (checkedRow.endsWith(oldComponentPath)) {
              return action.payload.pathsToTargetContainer.map((pathToContainer) =>
                PathUtilities.createFrom(pathToContainer, movedComponentInsertion.objectGuid),
              );
            }
          }

          return [checkedRow];
        }),
      );
      reconcileCheckAnchor(state);

      // a selected moved component's path and parent are no longer valid
      const { selectedPath } = state;
      if (
        selectedPath != null &&
        getMovedComponentInsertions(action.payload).some((insertion) =>
          selectedPath.endsWith(PathUtilities.createFromInsertion(insertion)),
        )
      ) {
        state.selectedPath = null;
        state.selectedParentGuid = null;
      }
    });

    builder.addCase(projectArchived, (state, action) => {
      state.checkedRows = state.checkedRows.filter(
        (rowPath) => PathUtilities.getProjectGuid(rowPath) !== action.payload.guid,
      );
      reconcileCheckAnchor(state);

      if (state.openProjectGuid === action.payload.guid) {
        state.openProjectGuid = null;
        state.focusProjectNameOnDrawerOpen = false;
      }

      if (state.selectedPath != null && PathUtilities.getProjectGuid(state.selectedPath) === action.payload.guid) {
        state.selectedPath = null;
        state.selectedParentGuid = null;
      }
    });

    builder.addCase(projectSynchronized, (state, action) => {
      if (state.selectedPath == null) return;

      const selectedModelGuid = PathUtilities.getGuid(state.selectedPath);
      const selectedProjectGuid = PathUtilities.getProjectGuid(state.selectedPath);

      // If the project is selected, we can't compare insertion count like we do below. Deselecting projects when they
      // are removed is handled above in the extra reducer for `projectArchived`.
      if (selectedModelGuid === selectedProjectGuid) return;

      const synchronizationPayload = action.payload[selectedProjectGuid];

      // If no information was synchronized related to the currently selected project, we have no updates to make
      if (!synchronizationPayload) return;

      if (synchronizationPayload.expiredModelGuids.includes(selectedModelGuid)) {
        const insertionsPostSync = synchronizationPayload.incomingInsertions.filter(
          (insertion) => insertion.objectGuid === selectedModelGuid,
        );

        if (insertionsPostSync.length === 0) {
          state.selectedPath = null;
          state.selectedParentGuid = null;
        }
      }
    });

    builder.addMatcher(
      isAnyOf(searchChanged, createdMonthChanged, statusChanged, assigneeChanged, filtersCleared),
      (state) => {
        state.pageIndex = 0;
      },
    );
  },
});

export const {
  rowExpanded,
  rowsExpanded,
  rowCollapsed,
  rowsCollapsed,
  rowsChecked,
  rowCheckToggled,
  rowsUnchecked,
  allRowsCollapsed,
  rowSelected,
  checkedRowsCleared,
  detailDrawerClosed,
  projectNameFocusConsumed,
  sealLoadingStartedForProject,
  sealLoadingFinishedForProject,
  sortColumnToggled,
  pageChanged,
} = gridSlice.actions;
export const grid = gridSlice.reducer;
